import BlockIcon from '@mui/icons-material/Block';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ColoredAvatar from 'core/components/ColoredAvatar';
import { getFullName as getUserFullName } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import { useAsync } from 'hooks/useAsync';
import CardedPage from 'layout/pages/CardedPage';
import { isNil } from 'lodash';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import userService from 'services/userService';
import UsersPageHeader from './UsersPageHeader';
import type { GridColDef, GridEventListener } from '@mui/x-data-grid';
import type { OrderActionEnum } from 'models/enums/orderActionEnum';
import type { User } from 'models/user/user';

export default function UsersPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = React.useState({
    page: +(searchParams.get('pageIndex') ?? 0),
    pageSize: +(searchParams.get('pageSize') ?? 25),
  });
  const [searchText, setSearchText] = React.useState(
    searchParams.get('searchText') ?? ''
  );

  // Add state for blocked filter
  const [blockedFilter, setBlockedFilter] = React.useState<boolean | null>(
    searchParams.get('blocked') === 'true'
      ? true
      : searchParams.get('blocked') === 'false'
      ? false
      : null
  );

  // Add temporary state for filter selection
  const [tempBlockedFilter, setTempBlockedFilter] = React.useState<boolean | null>(
    blockedFilter
  );

  // Add state for filter popover
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const filterPopoverOpen = Boolean(filterAnchorEl);

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setFilterAnchorEl(event.currentTarget);
    // Reset temporary filter to current filter when opening popover
    setTempBlockedFilter(blockedFilter);
  };

  const handleFilterClose = (): void => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    // Update only the temporary filter
    setTempBlockedFilter(value === 'all' ? null : value === 'true');
  };

  const handleFilterApply = (): void => {
    // Apply the temporary filter to the actual filter
    setBlockedFilter(tempBlockedFilter);
    // Reset to first page when applying a new filter
    setPaginationModel({
      ...paginationModel,
      page: 0,
    });
    handleFilterClose();
  };

  const handleFilterReset = (): void => {
    // Reset both filters
    setTempBlockedFilter(null);
    setBlockedFilter(null);
    setPaginationModel({
      ...paginationModel,
      page: 0,
    });
    handleFilterClose();
  };

  const queryOptions = React.useMemo(
    () => ({
      pageIndex: paginationModel.page,
      pageSize: paginationModel.pageSize,
      filterText: searchText,
      blocked: blockedFilter, // Add blocked filter to query options
    }),
    [paginationModel, searchText, blockedFilter]
  );

  const { loading, result } = useAsync(
    async () =>
      await userService.getUsersAsync({
        ...queryOptions,
        blocked: queryOptions.blocked ?? undefined,
      }),
    [queryOptions]
  );
  const [rowCountState, setRowCountState] = React.useState(result?.totalCount ?? 0);

  React.useEffect(() => {
    const params: Record<string, string> = {
      pageIndex: String(queryOptions.pageIndex),
      pageSize: String(queryOptions.pageSize),
      searchText: queryOptions.filterText,
    };

    // Add blocked parameter to URL only if it's not null
    if (blockedFilter !== null) {
      params.blocked = String(blockedFilter);
    }

    setSearchParams(params);
  }, [setSearchParams, queryOptions, blockedFilter]);

  React.useEffect(() => {
    setRowCountState((prevRowCountState: number) =>
      !isNil(result?.totalCount) ? result?.totalCount ?? 0 : prevRowCountState
    );
  }, [result?.totalCount, setRowCountState]);

  const getActionAccessesNamesRow = (accesses: OrderActionEnum[]): string =>
    accesses.map((a) => getOrderActionName(a)).join(', ');

  const columns: Array<GridColDef<User>> = [
    {
      field: 'avatar',
      width: 60,
      display: 'flex',
      renderCell: (params) =>
        params.row.blocked ? (
          <BlockIcon color='error' sx={{ fontSize: '40px', lineHeight: '40px' }} />
        ) : (
          <ColoredAvatar alt={params.row.surname}>{params.row.surname[0]}</ColoredAvatar>
        ),
    },
    {
      field: 'name',
      flex: 1,
      display: 'flex',
      renderCell: (params) => (
        <div>
          <Typography
            color={params.row.blocked ? 'error' : undefined}
            sx={{ display: 'flex', alignItems: 'center' }}>
            {!isNil(params.value) && getUserFullName(params.row)}
          </Typography>
          <Typography variant='caption' color={'textSecondary'}>
            {getActionAccessesNamesRow(params.row?.actionAccesses)}
          </Typography>
        </div>
      ),
    },
  ];

  const rowClickHandle: GridEventListener<'rowClick'> = (params) => {
    navigate(params.id.toString());
  };

  const createUser = (): void => {
    navigate('new/edit');
  };

  const filterButton = (
    <>
      <Tooltip title='Фильтр'>
        <IconButton
          onClick={handleFilterClick}
          color={blockedFilter !== null ? 'primary' : 'default'}>
          <FilterListIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={filterPopoverOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <FormControl sx={{ p: 2, minWidth: 200 }}>
          <Typography variant='subtitle1' gutterBottom>
            Статус пользователя
          </Typography>
          <RadioGroup
            value={tempBlockedFilter === null ? 'all' : String(tempBlockedFilter)}
            onChange={handleFilterChange}>
            <FormControlLabel value='all' control={<Radio />} label='Все' />
            <FormControlLabel value='false' control={<Radio />} label='Активные' />
            <FormControlLabel value='true' control={<Radio />} label='Заблокированные' />
          </RadioGroup>
          <Button variant='contained' onClick={handleFilterApply} sx={{ mt: 2, mr: 1 }}>
            Применить
          </Button>
          <Button variant='outlined' onClick={handleFilterReset} sx={{ mt: 2 }}>
            Сбросить
          </Button>
        </FormControl>
      </Popover>
    </>
  );

  return (
    <>
      <CardedPage
        title='Сотрудники'
        disablePadding
        header={
          <UsersPageHeader
            searchTextInit={searchText}
            onSearchChanged={setSearchText}
            onCreateClick={createUser}
            filterButton={filterButton}
          />
        }>
        <DataGrid
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              border: 'none',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
          rows={result?.items ?? []}
          rowCount={rowCountState}
          loading={loading}
          disableColumnMenu={true}
          rowHeight={70}
          autoHeight
          hideFooterSelectedRowCount
          disableRowSelectionOnClick
          onRowClick={rowClickHandle}
          columnHeaderHeight={0}
          paginationModel={paginationModel}
          paginationMode='server'
          onPaginationModelChange={setPaginationModel}
          columns={columns}
        />
      </CardedPage>
    </>
  );
}

// export default UsersPage;
